import { BotId } from '@feedyou-ai/feedbot-schema'
import { getHostingResourceGroupId, getNativeResourceGroupId } from '@feedyou/utils'

import { confirm, prompt } from '../../../../../lib/bootbox'

type Results = { confirmed: boolean; immediate: boolean }

export async function confirmDeletion(
  botId: string,
  isAdmin: boolean,
  hasDevEnv: boolean,
  testName: string
): Promise<Results> {
  if (!isAdmin) {
    const confirmed = await confirm({
      ...getTitleAndMessage(botId, hasDevEnv, testName),
      buttons: {
        confirm: { label: 'Delete bot', className: 'btn-negative' },
        cancel: { label: 'Cancel' }
      }
    })

    return { confirmed, immediate: false }
  }

  const bootboxResult = await prompt({
    ...getTitleAndMessage(botId, hasDevEnv, testName),
    inputType: 'checkbox',
    inputOptions: [{ text: 'Delete immediately (available to Feedies only)', value: '1' }],
    buttons: {
      confirm: { label: 'Delete bot', className: 'btn-negative' },
      cancel: { label: 'Cancel' }
    }
  })

  if (bootboxResult === null) {
    return { confirmed: false, immediate: false }
  }

  const isDeleteImmediate = !!bootboxResult?.length

  if (!isDeleteImmediate) {
    return { confirmed: true, immediate: false }
  }

  const isInstantHardDeleteReConfirmed = await confirmInstantHardDeletion(botId)

  if (!isInstantHardDeleteReConfirmed) {
    return { confirmed: false, immediate: false }
  }

  return { confirmed: true, immediate: isDeleteImmediate }
}

const getTitleAndMessage = (botId: BotId, hasDevEnv: boolean, testName: string) => {
  return {
    title: '⚠️ Permanently delete bot',
    message: `
			Are you sure you want to delete bot <strong>${botId}</strong>
			${hasDevEnv ? `<strong>(including ${testName} environment)</strong>` : ''}? 
			
			After 30 days, it will no longer be possible to renew it. In addition, the hosting, user data and the entire communication tree structure, including NLP models, will be deleted.`
  }
}

export function confirmDeletionDespiteNonNativeHostingId(
  botId: string,
  botApiUrl: string
): Promise<boolean> {
  const botHostingId = getHostingResourceGroupId(botApiUrl)
  const nativeBotHostingId = getNativeResourceGroupId(botId, window.REALTIME_NAMESPACE || '')

  return confirm({
    title: `❌ Hosting mismatch with API URL`,
    message: `Your bot’s hosting ID ${nativeBotHostingId} does not match with the API URL defined in the bot settings JSON (${botApiUrl}). 
							It might happen when you copy the API URL from another bot. 
							Hosting <strong>${botHostingId}</strong> will be deleted.<br /><br />
							Do you really want to continue? 
							If you are not sure contact <a target="_blank" rel="noopener noreferrer" href="https://feedyou.atlassian.net/servicedesk/customer/portals">our support</a>.`,
    buttons: {
      confirm: {
        label: 'Delete bot anyway',
        className: 'btn-negative'
      },
      cancel: {
        label: 'Close'
      }
    }
  })
}

async function confirmInstantHardDeletion(botId: string): Promise<boolean> {
  while (true) {
    const commandPrompt = await prompt({
      title: '💡 Confirm permanent bot deletion',
      message: `Please enter command "delete ${botId}" to verify that you really know what are you doing. It won't be possible to undo this operation anytime later.`
    })

    if (commandPrompt === null) {
      return false
    }

    if (commandPrompt === `delete ${botId}`) {
      return true
    }
  }
}
